// A loading ramp — a 2x2 freight dock: a raised timber deck on stone footings
// with a sloped tread-plate ramp down the road-facing front (+z), a loading
// gantry over the deck, guard rails, and goods stacked ready to ship. Ported 1:1
// from age-of-mcp's ramp.ts: the shared foundation/scaffold/crate/barrel come
// from the library, the deck/ramp/gantry/rails are local helpers, and the four
// build stages are transcribed from stage(s).
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, crate, barrel, type Part } from "../../lib";

const STONE = "#6a6a6a"; // footings
const DECK = "#8a5f34"; // timber deck
const POST = "#5e3f24"; // posts / frame
const RAIL = "#6b4a26"; // guard rails
const BEAM = "#4a4540"; // steel gantry beam
const PLATE = "#3a3a3a"; // ramp tread plate

const BASE = 0.14;
const DECK_Y = 0.46; // top of the raised deck
const SPAN = 1.7; // deck span (of the 2x2 plot)

// The raised deck slab + its supporting posts.
function deck(): Part[] {
  const out: Part[] = [
    { geometry: new THREE.BoxGeometry(SPAN, 0.1, SPAN), color: DECK, position: [0, DECK_Y - 0.05, 0] },
  ];
  const o = SPAN / 2 - 0.1;
  for (const [x, z] of [[-o, -o], [o, -o], [-o, o], [o, o]] as [number, number][]) {
    out.push({ geometry: new THREE.BoxGeometry(0.12, DECK_Y - 0.1, 0.12), color: POST, position: [x, (DECK_Y - 0.1) / 2 + BASE, z] });
  }
  return out;
}

// A sloped tread-plate ramp down the road-facing front (+z) from deck to ground.
function rampSlope(): Part {
  const rise = DECK_Y - 0.06, run = 0.5;
  const len = Math.hypot(rise, run), ang = Math.atan2(rise, run);
  return {
    geometry: new THREE.BoxGeometry(SPAN - 0.2, 0.06, len),
    color: PLATE,
    position: [0, DECK_Y / 2, SPAN / 2 + run / 2 - 0.05],
    rotation: [-(Math.PI / 2 - ang), 0, 0],
  };
}

// A loading gantry: two posts + a cross beam over the back of the deck, with a
// hanging block-and-hook.
function gantry(): Part[] {
  const gy = DECK_Y, h = 0.66, x = SPAN / 2 - 0.25, z = -0.4;
  return [
    { geometry: new THREE.BoxGeometry(0.1, h, 0.1), color: BEAM, position: [-x, gy + h / 2, z] },
    { geometry: new THREE.BoxGeometry(0.1, h, 0.1), color: BEAM, position: [x, gy + h / 2, z] },
    { geometry: new THREE.BoxGeometry(2 * x + 0.12, 0.1, 0.1), color: BEAM, position: [0, gy + h, z] },
    { geometry: new THREE.BoxGeometry(0.06, 0.18, 0.06), color: PLATE, position: [0, gy + h - 0.18, z] },
    { geometry: new THREE.BoxGeometry(0.04, 0.1, 0.04), color: "#222", position: [0, gy + h - 0.34, z] },
  ];
}

// Guard rails along the back and sides of the deck (the front is the ramp).
function rails(): Part[] {
  const ry = DECK_Y + 0.16, o = SPAN / 2 - 0.05;
  return [
    { geometry: new THREE.BoxGeometry(SPAN, 0.04, 0.04), color: RAIL, position: [0, ry, -o] },
    { geometry: new THREE.BoxGeometry(0.04, 0.04, SPAN), color: RAIL, position: [-o, ry, 0] },
    { geometry: new THREE.BoxGeometry(0.04, 0.04, SPAN), color: RAIL, position: [o, ry, 0] },
  ];
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(1.94)];
  if (s === 1) return [foundationSlab(1.94), ...scaffold(SPAN, 0.5)];
  if (s === 2) return [foundationSlab(1.94), ...deck(), ...scaffold(SPAN, 0.9)];
  return [
    // Stone footings + raised timber deck.
    { geometry: new THREE.BoxGeometry(1.94, BASE, 1.94), color: STONE, position: [0, BASE / 2, 0] },
    ...deck(),
    rampSlope(),
    ...rails(),
    ...gantry(),
    // Goods stacked on the deck, ready to ship.
    crate(-0.35, -0.3, 0.3),
    crate(0.0, -0.32, 0.26),
    crate(-0.05, -0.05, 0.24),
    barrel(0.42, -0.28, DECK_Y, 0.26),
  ];
}

export const ramp = defineModel({
  id: "ramp",
  tiles: { w: 2, h: 2 },
  batchId: "standard",
  name: "Ramp",
  tags: ["building", "ramp", "low-poly", "age-of-mcp", "classic"],
  summary: "Low-poly ramp.",
  description: "A raised freight dock.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
